/**
 * Admin Weather Source Health — radar health formatting and copy helpers.
 */
import { SIATA_SANTA_ELENA_RADAR_INVENTORY } from "../constants/siataSantaElenaRadarPlan.js";
import { AEROCIVIL_AVIATION_RADAR_INVENTORY } from "../constants/aerocivilAviationRadarPlan.js";

export const RADAR_SOURCE_LABELS = {
  ideam_bart_composite: "IDEAM Imagen Compuesta",
  rainviewer: "RainViewer backup",
  ideam_ospa: "Legacy IDEAM OSPA",
  siata_santa_elena: "SIATA Santa Elena Radar",
  aerocivil_aviation_radar: "Aerocivil Aviation Radar",
};

export const RADAR_TRUST_LABELS = {
  ideam_bart_composite: "Official",
  rainviewer: "Backup",
  ideam_ospa: "Official / Legacy",
  siata_santa_elena: "Local official",
  aerocivil_aviation_radar: "Official (aeronautical)",
};

export const ADMIN_SOURCE_MODE_LABELS = {
  preferred: "Preferred (IDEAM + RainViewer fallback)",
  official_only: "IDEAM official only",
  ideam_bart_only: "IDEAM official only",
  backup_only: "RainViewer backup only",
  rainviewer_only: "RainViewer backup only",
  valley_preferred: "Valley Preferred (SIATA → IDEAM → RainViewer)",
  siata_local_only: "SIATA local only",
};

export const ADMIN_FALLBACK_REASON_LABELS = {
  official_unavailable: "Official radar unavailable",
  siata_outside_coverage: "Outside SIATA coverage area",
  siata_unavailable: "SIATA radar unavailable",
  siata_stale: "SIATA radar stale",
  siata_disabled: "SIATA feature disabled",
  siata_not_configured: "SIATA not configured",
  rainviewer_backup_active: "RainViewer backup active",
};

export const PLANNED_WEATHER_RADAR_SOURCES = [
  {
    id: AEROCIVIL_AVIATION_RADAR_INVENTORY.sourceKey,
    source: AEROCIVIL_AVIATION_RADAR_INVENTORY.displayName,
    sourceKey: AEROCIVIL_AVIATION_RADAR_INVENTORY.registrySourceCode,
    type: "Official aeronautical radar",
    status: "approved_development",
    integration: "client_layer_complete",
    message:
      "Client layer complete (A4). Enable AEROCIVIL_RADAR_ENABLED and run discovery/sync for live health.",
    planAnchor: "#aerocivil-aviation-radar-plan",
    approvalDocPath: AEROCIVIL_AVIATION_RADAR_INVENTORY.approvalDocPath,
    planPath: "/admin/weather/sources#radar-health",
    actions: ["view-plan", "view-approval-doc"],
  },
  {
    id: SIATA_SANTA_ELENA_RADAR_INVENTORY.sourceKey,
    source: SIATA_SANTA_ELENA_RADAR_INVENTORY.displayName,
    sourceKey: SIATA_SANTA_ELENA_RADAR_INVENTORY.registrySourceCode,
    type: "Local official radar",
    status: "approved_development",
    integration: "provider_configured",
    message:
      "Provider configured. Enable SIATA_SANTA_ELENA_RADAR_ENABLED and run discovery/sync to activate.",
    planAnchor: "#siata-santa-elena-radar-plan",
    approvalDocPath: SIATA_SANTA_ELENA_RADAR_INVENTORY.approvalDocPath,
    planPath: "/admin/weather/sources#radar-health",
    actions: ["view-plan", "view-approval-doc"],
  },
];

export const UNMONITORED_WEATHER_SOURCES = [
  {
    id: "satellite_imerg",
    source: "Satellite precipitation / IMERG",
    type: "Satellite estimate",
  },
  {
    id: "siata_sensors",
    source: "SIATA sensors",
    type: "Local sensors",
  },
  {
    id: "siata_alerts",
    source: "SIATA alerts",
    type: "Alerts",
  },
  {
    id: "community_reports",
    source: "Community reports",
    type: "Community",
  },
];

export function formatFrameAge(minutes) {
  if (minutes == null || Number.isNaN(minutes)) {
    return "—";
  }

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

export function resolveSourceLabel(sourceKey, health) {
  if (!sourceKey) {
    return "—";
  }

  const sources = health?.sources || {};
  if (sourceKey === sources.ideamBart?.source) {
    return sources.ideamBart.sourceLabel;
  }
  if (sourceKey === sources.rainViewer?.source) {
    return sources.rainViewer.sourceLabel;
  }
  if (sourceKey === sources.legacyOspa?.source) {
    return sources.legacyOspa.sourceLabel;
  }
  if (sourceKey === sources.siataSantaElena?.source || sourceKey === sources.siataSantaElena?.sourceKey) {
    return sources.siataSantaElena.sourceLabel;
  }
  if (sourceKey === sources.aerocivilAviation?.sourceKey || sourceKey === "aerocivilAviation") {
    return sources.aerocivilAviation.sourceLabel;
  }

  return RADAR_SOURCE_LABELS[sourceKey] || sourceKey.replace(/_/g, " ");
}

export function resolveTrustLabel(sourceKey) {
  return RADAR_TRUST_LABELS[sourceKey] || "—";
}

export function resolveAdminSourceModeLabel(sourceMode) {
  if (!sourceMode) {
    return "—";
  }
  return ADMIN_SOURCE_MODE_LABELS[sourceMode] || sourceMode.replace(/_/g, " ");
}

export function resolveAdminFallbackReasonLabel(fallbackReason) {
  if (!fallbackReason) {
    return "—";
  }
  return ADMIN_FALLBACK_REASON_LABELS[fallbackReason] || fallbackReason.replace(/_/g, " ");
}

export function resolveRenderedTrustBadge(health) {
  const rendered = health?.renderedSource;

  if (!rendered || health?.status === "unavailable") {
    return "Unavailable";
  }

  return resolveTrustLabel(rendered);
}

export function resolveFallbackBehaviorCopy(health) {
  if (!health) {
    return "Radar health has not been checked yet.";
  }

  const { sourceMode, fallbackActive, fallbackReason, renderedSource } = health;
  const officialUnavailable = ["unavailable", "empty", "error"].includes(
    health?.sources?.ideamBart?.status
  );

  if (sourceMode === "official_only" || sourceMode === "ideam_bart_only") {
    return "Official-only mode is selected, so fallback is disabled.";
  }

  if (sourceMode === "backup_only" || sourceMode === "rainviewer_only") {
    return "Backup-only mode is selected. RainViewer is the intended rendered source.";
  }

  if (sourceMode === "valley_preferred") {
    const siata = health?.sources?.siataSantaElena;
    if (renderedSource === "siata_santa_elena") {
      return "Valley Preferred is using SIATA local radar.";
    }
    if (renderedSource === "ideam_bart_composite") {
      return "Valley Preferred fell back to IDEAM official radar (SIATA unavailable or outside coverage).";
    }
    if (renderedSource === "rainviewer") {
      return "Valley Preferred is using RainViewer backup because SIATA and IDEAM are unavailable.";
    }
    if (siata?.status === "disabled") {
      return "Valley Preferred cannot use SIATA because the feature is disabled.";
    }
    return "Valley Preferred source chain: SIATA → IDEAM → RainViewer backup.";
  }

  if (sourceMode === "siata_local_only") {
    return renderedSource === "siata_santa_elena"
      ? "SIATA local-only mode is active."
      : "SIATA local-only mode selected, but SIATA radar is unavailable. No fallback is active.";
  }

  if (fallbackActive || renderedSource === "rainviewer") {
    const reason = fallbackReason
      ? ` (${resolveAdminFallbackReasonLabel(fallbackReason)})`
      : "";
    return `Preferred mode is currently using RainViewer because official radar is unavailable${reason}.`;
  }

  if (officialUnavailable && !fallbackActive) {
    return "Official radar is degraded, but fallback is not currently active.";
  }

  return "Preferred mode is using official radar.";
}

export function buildRadarDiagnosticsPayload(health) {
  if (!health) {
    return null;
  }

  return {
    checkedAt: health.checkedAt,
    status: health.status,
    message: health.message,
    preferredSource: health.preferredSource,
    renderedSource: health.renderedSource,
    sourceMode: health.sourceMode,
    fallbackActive: health.fallbackActive,
    fallbackReason: health.fallbackReason,
    sources: health.sources,
  };
}
